// src/pages/modals/POS_VentasContingencia.js
'use strict';

import React, { useCallback, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  ErrorOutline as ErrorOutlineIcon,
  PendingActions as PendingActionsIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Print as PrintIcon,
  ReceiptLong as ReceiptLongIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';

import PinConfirmModal from './PinConfirmModal';

const formatter = new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'CLP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function formatMoney(value) {
  const n = Number(value);
  return formatter.format(Number.isFinite(n) ? n : 0);
}

function formatDate(value) {
  if (!value) return 'Sin fecha';

  try {
    return new Date(value).toLocaleString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Sin fecha';
  }
}

function getNombreCliente(cliente) {
  if (!cliente) return 'Cliente genérico';

  if (cliente.nombres && cliente.apellidos) {
    return `${cliente.nombres} ${cliente.apellidos}`;
  }

  if (cliente.razon_social) return cliente.razon_social;
  if (cliente.nombre_mostrar) return cliente.nombre_mostrar;
  if (cliente.fullname) return cliente.fullname;
  if (cliente.fullName) return cliente.fullName;
  if (cliente.name) return cliente.name;
  if (cliente.nombre) return cliente.nombre;

  return 'Cliente genérico';
}

function getRutCliente(cliente) {
  if (!cliente) return '';

  const rut = cliente.rut || '';
  const dv = cliente.dv || '';

  if (rut && dv && !String(rut).includes('-')) {
    return `${rut}-${dv}`;
  }

  return rut;
}

function getCantidadItems(items) {
  if (!Array.isArray(items)) return 0;
  return items.length;
}

function getResumenProductos(items) {
  if (!Array.isArray(items) || items.length === 0) {
    return 'Sin productos';
  }

  const primeros = items
    .slice(0, 3)
    .map((item) => {
      const nombre = item.name || item.nombre || 'Producto';

      const cantidad = item.isWeight
        ? `${Number(item.quantity || item.cantidad || 0).toFixed(3)} kg`
        : `x${Number(item.quantity || item.cantidad || 0)}`;

      return `${nombre} ${cantidad}`;
    })
    .join(' · ');

  if (items.length > 3) {
    return `${primeros} · +${items.length - 3} más`;
  }

  return primeros;
}

function getServerData(record) {
  return record?.serverData || record?.server_data || null;
}

function getPrimaryServerResult(record) {
  const data = getServerData(record);
  if (!data) return null;

  const offlineId = record?.offlineId || record?.id || null;

  const results = Array.isArray(data?.results)
    ? data.results
    : Array.isArray(data?.data?.results)
      ? data.data.results
      : Array.isArray(data?.resultado?.results)
        ? data.resultado.results
        : Array.isArray(data?.resultados)
          ? data.resultados
          : [];

  if (results.length > 0) {
    return (
      results.find((row) => row?.offlineId === offlineId) ||
      results.find((row) => row?.ok === true) ||
      results[0]
    );
  }

  return data;
}

function getServerVenta(record) {
  const data = getServerData(record);
  const result = getPrimaryServerResult(record);

  return (
    result?.venta ||
    result?.data?.venta ||
    result?.resultado?.venta ||
    result?.result?.venta ||
    result?.response?.venta ||
    data?.venta ||
    data?.data?.venta ||
    data?.resultado?.venta ||
    data?.result?.venta ||
    data?.response?.venta ||
    null
  );
}

function getServerDte(record) {
  const data = getServerData(record);
  const result = getPrimaryServerResult(record);

  return (
    record?.dteInfo ||
    record?.dte_info ||
    result?.dte ||
    result?.DTE ||
    result?.data?.dte ||
    result?.data?.DTE ||
    result?.resultado?.dte ||
    result?.resultado?.DTE ||
    result?.result?.dte ||
    result?.response?.dte ||
    data?.dte ||
    data?.DTE ||
    data?.data?.dte ||
    data?.data?.DTE ||
    data?.resultado?.dte ||
    data?.resultado?.DTE ||
    data?.result?.dte ||
    data?.response?.dte ||
    null
  );
}

function getPdfUrlServidor(record) {
  const data = getServerData(record);
  const result = getPrimaryServerResult(record);
  const dte = getServerDte(record);

  return (
    record?.pdfUrl ||
    record?.pdf_url ||
    dte?.pdf_url ||
    dte?.pdfUrl ||
    dte?.url_pdf ||
    dte?.urlPdf ||
    dte?.pdf ||
    result?.pdf_url ||
    result?.pdfUrl ||
    result?.url_pdf ||
    result?.urlPdf ||
    result?.pdf ||
    result?.data?.pdf_url ||
    result?.data?.pdfUrl ||
    result?.data?.url_pdf ||
    result?.data?.urlPdf ||
    result?.data?.pdf ||
    result?.resultado?.pdf_url ||
    result?.resultado?.pdfUrl ||
    result?.resultado?.url_pdf ||
    result?.resultado?.urlPdf ||
    result?.resultado?.pdf ||
    data?.pdf_url ||
    data?.pdfUrl ||
    data?.url_pdf ||
    data?.urlPdf ||
    data?.pdf ||
    data?.data?.pdf_url ||
    data?.data?.pdfUrl ||
    data?.data?.url_pdf ||
    data?.data?.urlPdf ||
    data?.data?.pdf ||
    data?.resultado?.pdf_url ||
    data?.resultado?.pdfUrl ||
    data?.resultado?.url_pdf ||
    data?.resultado?.urlPdf ||
    data?.resultado?.pdf ||
    null
  );
}

function getVentaIdServidor(record) {
  const data = getServerData(record);
  const result = getPrimaryServerResult(record);
  const venta = getServerVenta(record);
  const dte = getServerDte(record);

  return (
    record?.ventaId ??
    record?.venta_id ??
    venta?.id_venta ??
    venta?.venta_id ??
    venta?.idVenta ??
    venta?.ventaId ??
    venta?.id ??
    dte?.id_venta ??
    dte?.venta_id ??
    dte?.idVenta ??
    dte?.ventaId ??
    result?.id_venta ??
    result?.venta_id ??
    result?.idVenta ??
    result?.ventaId ??
    result?.id ??
    result?.data?.id_venta ??
    result?.data?.venta_id ??
    result?.data?.idVenta ??
    result?.data?.ventaId ??
    result?.data?.id ??
    result?.resultado?.id_venta ??
    result?.resultado?.venta_id ??
    result?.resultado?.idVenta ??
    result?.resultado?.ventaId ??
    result?.resultado?.id ??
    data?.id_venta ??
    data?.venta_id ??
    data?.idVenta ??
    data?.ventaId ??
    data?.id ??
    data?.data?.id_venta ??
    data?.data?.venta_id ??
    data?.data?.idVenta ??
    data?.data?.ventaId ??
    data?.data?.id ??
    data?.resultado?.id_venta ??
    data?.resultado?.venta_id ??
    data?.resultado?.idVenta ??
    data?.resultado?.ventaId ??
    data?.resultado?.id ??
    null
  );
}

function getTipoDteServidor(record) {
  const data = getServerData(record);
  const result = getPrimaryServerResult(record);
  const dte = getServerDte(record);

  return (
    dte?.tipo_dte ??
    dte?.TipoDTE ??
    dte?.tipoDte ??
    dte?.tipo ??
    result?.tipo_dte ??
    result?.TipoDTE ??
    result?.tipoDte ??
    result?.tipo ??
    result?.data?.tipo_dte ??
    result?.data?.TipoDTE ??
    result?.resultado?.tipo_dte ??
    result?.resultado?.TipoDTE ??
    data?.tipo_dte ??
    data?.TipoDTE ??
    data?.tipoDte ??
    data?.tipo ??
    data?.data?.tipo_dte ??
    data?.data?.TipoDTE ??
    data?.resultado?.tipo_dte ??
    data?.resultado?.TipoDTE ??
    null
  );
}

function getDocumentoLabel(record) {
  const tipoDte = String(getTipoDteServidor(record) || '');
  const documentType = String(
    record?.documentType ||
      record?.document_type ||
      record?.payload?.documentType ||
      record?.payload?.document_type ||
      record?.payload?.tipo_documento ||
      ''
  ).toLowerCase();

  if (tipoDte === '33') return 'Factura DTE 33';
  if (tipoDte === '34') return 'Factura Exenta DTE 34';
  if (tipoDte === '39') return 'Boleta DTE 39';
  if (tipoDte === '41') return 'Boleta Exenta DTE 41';

  if (documentType === 'factura') return 'Factura';
  if (documentType === 'factura_exenta') return 'Factura Exenta';
  if (documentType === 'boleta_exenta') return 'Boleta Exenta';

  return 'Boleta';
}

function getTotalRecord(record) {
  return (
    record?.totals?.totalPayable ??
    record?.totals?.total_pagar ??
    record?.totals?.total_bruto ??
    record?.totals?.totalGross ??
    record?.payment?.amount_paid ??
    record?.payment?.monto_pagado ??
    record?.payload?.amount_paid ??
    record?.payload?.monto_pagado ??
    record?.payload?.total_amount ??
    record?.payload?.total ??
    0
  );
}

function getEstadoRecord(record, isSyncing = false) {
  if (isSyncing) {
    return {
      key: 'sincronizando',
      label: 'Sincronizando',
      color: 'info',
      icon: <SyncIcon fontSize="small" />,
    };
  }

  if (record?.synced) {
    return {
      key: 'sincronizada',
      label: 'Sincronizada',
      color: 'success',
      icon: <CheckCircleOutlineIcon fontSize="small" />,
    };
  }

  if (record?.syncRequiresReview === true) {
    return {
      key: 'revision',
      label: 'Requiere revisión',
      color: 'warning',
      icon: <WarningAmberIcon fontSize="small" />,
    };
  }

  if (record?.syncError) {
    return {
      key: 'error',
      label: 'Con error',
      color: 'error',
      icon: <ErrorOutlineIcon fontSize="small" />,
    };
  }

  return {
    key: 'pendiente',
    label: 'Pendiente',
    color: 'warning',
    icon: <PendingActionsIcon fontSize="small" />,
  };
}

function getItemsRecord(record) {
  if (Array.isArray(record?.itemsSnapshot)) return record.itemsSnapshot;
  if (Array.isArray(record?.items)) return record.items;
  if (Array.isArray(record?.payload?.items)) return record.payload.items;
  if (Array.isArray(record?.payload?.detalle)) return record.payload.detalle;
  return [];
}

function getCustomerRecord(record) {
  return (
    record?.customerSnapshot ||
    record?.customer ||
    record?.cliente ||
    record?.payload?.cliente ||
    record?.payload?.customer ||
    null
  );
}

function getOfflineIdRecord(record) {
  return record?.offlineId || record?.id || record?.payload?.offlineId || '';
}

const POS_VentasContingencia = ({
  open,
  ventas = [],
  loading = false,
  syncing = false,
  syncingId = null,
  isOnline = true,
  onClose,
  onRefresh,
  onReintentar,
  onReintentarTodas,
  onReimprimirContingencia,
  onImprimirDte,
  onEliminar,
  onLimpiarSincronizadas,
}) => {
  const [pinOpen, setPinOpen] = useState(false);
  const [accionPendiente, setAccionPendiente] = useState(null);

  const ventasOrdenadas = useMemo(() => {
    return [...(Array.isArray(ventas) ? ventas : [])].sort((a, b) => {
      const da = new Date(a.createdAt || a.created_at || 0).getTime();
      const db = new Date(b.createdAt || b.created_at || 0).getTime();
      return db - da;
    });
  }, [ventas]);

  const resumen = useMemo(() => {
    return ventasOrdenadas.reduce(
      (acc, record) => {
        if (record.synced) acc.sincronizadas += 1;
        else if (record.syncRequiresReview === true) acc.revision += 1;
        else if (record.syncError) acc.errores += 1;
        else acc.pendientes += 1;
        return acc;
      },
      {
        pendientes: 0,
        errores: 0,
        revision: 0,
        sincronizadas: 0,
      }
    );
  }, [ventasOrdenadas]);

  const totalPendienteAccion = resumen.pendientes + resumen.errores + resumen.revision;

  const cerrarPin = useCallback(() => {
    setPinOpen(false);
    setAccionPendiente(null);
  }, []);

  const solicitarEliminar = useCallback((record) => {
    if (!record) return;
    setAccionPendiente({ tipo: 'eliminar', record });
    setPinOpen(true);
  }, []);

  const solicitarLimpiarSincronizadas = useCallback(() => {
    if (!resumen.sincronizadas) return;
    setAccionPendiente({ tipo: 'limpiar_sincronizadas', record: null });
    setPinOpen(true);
  }, [resumen.sincronizadas]);

  const ejecutarAccionAutorizada = useCallback(() => {
    if (!accionPendiente) {
      setPinOpen(false);
      return;
    }

    if (accionPendiente.tipo === 'eliminar' && accionPendiente.record) {
      onEliminar?.(accionPendiente.record);
    }

    if (accionPendiente.tipo === 'limpiar_sincronizadas') {
      onLimpiarSincronizadas?.();
    }

    setPinOpen(false);
    setAccionPendiente(null);
  }, [accionPendiente, onEliminar, onLimpiarSincronizadas]);

  const handleClosePrincipal = useCallback(() => {
    setPinOpen(false);
    setAccionPendiente(null);
    onClose?.();
  }, [onClose]);

  const resumenCards = [
    {
      key: 'pendientes',
      label: 'Pendientes',
      value: resumen.pendientes,
      color: 'warning',
      icon: <PendingActionsIcon fontSize="small" />,
      detail: 'Esperando envío',
    },
    {
      key: 'revision',
      label: 'Requieren revisión',
      value: resumen.revision,
      color: 'warning',
      icon: <WarningAmberIcon fontSize="small" />,
      detail: 'Acción manual',
    },
    {
      key: 'errores',
      label: 'Con error',
      value: resumen.errores,
      color: 'error',
      icon: <ErrorOutlineIcon fontSize="small" />,
      detail: 'Reintento disponible',
    },
    {
      key: 'sincronizadas',
      label: 'Sincronizadas',
      value: resumen.sincronizadas,
      color: 'success',
      icon: <CheckCircleOutlineIcon fontSize="small" />,
      detail: 'Enviadas al servidor',
    },
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClosePrincipal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3.5,
            overflow: 'hidden',
            maxHeight: '88vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2.5,
            py: 1.8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2.5,
                display: 'grid',
                placeItems: 'center',
                bgcolor: isOnline ? 'success.main' : 'error.main',
                color: '#fff',
                flexShrink: 0,
              }}
            >
              <ReceiptLongIcon />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
                  Ventas en contingencia
                </Typography>
                <Chip
                  size="small"
                  label={isOnline ? 'POS online' : 'Sin conexión'}
                  color={isOnline ? 'success' : 'error'}
                  variant="outlined"
                  sx={{ fontWeight: 800 }}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                Ventas cobradas localmente que deben validarse o sincronizarse con el servidor.
              </Typography>
            </Box>
          </Stack>

          <Tooltip title="Cerrar">
            <IconButton onClick={handleClosePrincipal} size="small">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>

        {/*
          El espacio del progreso queda reservado siempre. Así, cuando un
          chequeo de conexión o una sincronización cambia loading/syncing,
          el contenido del modal no sube ni baja y desaparece el parpadeo.
        */}
        <Box sx={{ height: 4, flexShrink: 0, bgcolor: 'background.paper' }}>
          {(loading || syncing) ? (
            <LinearProgress sx={{ height: 4 }} />
          ) : null}
        </Box>

        <DialogContent sx={{ px: 2.5, py: 2.25, bgcolor: 'grey.50' }}>
          <Stack spacing={2}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr 1fr',
                  sm: 'repeat(4, minmax(0, 1fr))',
                },
                gap: 1.25,
              }}
            >
              {resumenCards.map((item) => {
                const destacado = item.value > 0;

                return (
                  <Card
                    key={item.key}
                    variant="outlined"
                    sx={{
                      borderRadius: 2.5,
                      borderColor: destacado ? `${item.color}.main` : 'divider',
                      bgcolor: destacado ? 'action.hover' : 'background.paper',
                      boxShadow: 'none',
                    }}
                  >
                    <CardContent sx={{ p: '12px !important' }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 2,
                            display: 'grid',
                            placeItems: 'center',
                            bgcolor: destacado ? `${item.color}.main` : 'action.hover',
                            color: destacado ? '#fff' : 'text.secondary',
                            flexShrink: 0,
                          }}
                        >
                          {item.icon}
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 900, lineHeight: 1, fontSize: '1.15rem' }}
                          >
                            {item.value}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              fontWeight: 800,
                              lineHeight: 1.15,
                              mt: 0.25,
                            }}
                          >
                            {item.label}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>

            {!isOnline && (
              <Alert
                severity="warning"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={onRefresh}
                    disabled={loading || syncing}
                    sx={{ fontWeight: 800 }}
                  >
                    Verificar
                  </Button>
                }
              >
                El servidor no está disponible. Las ventas permanecen protegidas en este equipo.
              </Alert>
            )}

            {ventasOrdenadas.length === 0 ? (
              <Box
                sx={{
                  minHeight: 190,
                  display: 'grid',
                  placeItems: 'center',
                  textAlign: 'center',
                  bgcolor: 'background.paper',
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 3,
                  px: 3,
                  py: 4,
                }}
              >
                <Box>
                  <Box
                    sx={{
                      width: 58,
                      height: 58,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: 'action.hover',
                      color: 'success.main',
                      mx: 'auto',
                      mb: 1.5,
                    }}
                  >
                    <CheckCircleOutlineIcon sx={{ fontSize: 34 }} />
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                    Sin ventas de contingencia
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    No existen operaciones offline pendientes, con error o sincronizadas guardadas en este POS.
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Stack spacing={1.25}>
                {ventasOrdenadas.map((record) => {
                  const offlineId = getOfflineIdRecord(record);
                  const isRowSyncing =
                    Boolean(offlineId) &&
                    (String(syncingId || '') === String(offlineId) || record?.syncing === true);

                  const estado = getEstadoRecord(record, isRowSyncing);
                  const cliente = getCustomerRecord(record);
                  const items = getItemsRecord(record);
                  const ventaIdServidor = getVentaIdServidor(record);
                  const pdfUrlServidor = getPdfUrlServidor(record);
                  const total = getTotalRecord(record);
                  const documento = getDocumentoLabel(record);
                  const puedeImprimirDte = Boolean(
                    record.synced && (ventaIdServidor || pdfUrlServidor)
                  );

                  const bordeEstado =
                    estado.key === 'sincronizada'
                      ? 'success.main'
                      : estado.key === 'error'
                        ? 'error.main'
                        : estado.key === 'sincronizando'
                          ? 'info.main'
                          : 'warning.main';

                  return (
                    <Card
                      key={offlineId}
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        borderColor: 'divider',
                        borderLeft: '4px solid',
                        borderLeftColor: bordeEstado,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                        bgcolor: 'background.paper',
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Stack
                          direction={{ xs: 'column', md: 'row' }}
                          spacing={2}
                          alignItems={{ xs: 'stretch', md: 'center' }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack
                              direction="row"
                              spacing={0.75}
                              alignItems="center"
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                                {getNombreCliente(cliente)}
                              </Typography>

                              <Chip
                                size="small"
                                icon={estado.icon}
                                label={estado.label}
                                color={estado.color}
                                variant={estado.key === 'pendiente' ? 'outlined' : 'filled'}
                                sx={{ fontWeight: 800 }}
                              />

                              <Chip size="small" label={documento} variant="outlined" />

                              {ventaIdServidor && (
                                <Chip
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  label={`Venta #${ventaIdServidor}`}
                                />
                              )}
                            </Stack>

                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                              {getRutCliente(cliente) || 'Sin RUT informado'}
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                mt: 1,
                                color: 'text.primary',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {getResumenProductos(items)}
                            </Typography>

                            {record.syncError && (
                              <Alert
                                severity={record.syncRequiresReview === true ? 'warning' : 'error'}
                                sx={{ mt: 1.25, py: 0.35 }}
                              >
                                <strong>
                                  {record.syncRequiresReview === true
                                    ? 'Revisión manual requerida.'
                                    : 'No se pudo sincronizar.'}
                                </strong>{' '}
                                {record.syncError}
                                {record.syncErrorCode ? ` · ${record.syncErrorCode}` : ''}
                              </Alert>
                            )}

                            {record.synced && !puedeImprimirDte && (
                              <Alert severity="warning" sx={{ mt: 1.25, py: 0.35 }}>
                                La venta está sincronizada, pero todavía no hay DTE/PDF recuperable.
                              </Alert>
                            )}

                            <Stack
                              direction="row"
                              spacing={0.75}
                              flexWrap="wrap"
                              useFlexGap
                              sx={{ mt: 1.25 }}
                            >
                              <Chip
                                size="small"
                                label={`${getCantidadItems(items)} producto(s)`}
                                sx={{ bgcolor: 'grey.100' }}
                              />
                              <Chip
                                size="small"
                                label={`Creada ${formatDate(record.createdAt || record.created_at)}`}
                                sx={{ bgcolor: 'grey.100' }}
                              />
                              {(record.syncedAt || record.syncAt || record.synced_at) && (
                                <Chip
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  label={`Sincronizada ${formatDate(
                                    record.syncedAt || record.syncAt || record.synced_at
                                  )}`}
                                />
                              )}
                              {!record.synced && record.lastSyncAttemptAt && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`Último intento ${formatDate(record.lastSyncAttemptAt)}`}
                                />
                              )}
                            </Stack>

                            {offlineId && (
                              <Typography
                                variant="caption"
                                color="text.disabled"
                                sx={{
                                  display: 'block',
                                  mt: 1,
                                  fontFamily: 'monospace',
                                  overflowWrap: 'anywhere',
                                }}
                              >
                                {offlineId}
                              </Typography>
                            )}
                          </Box>

                          <Divider
                            flexItem
                            orientation="vertical"
                            sx={{ display: { xs: 'none', md: 'block' } }}
                          />

                          <Box sx={{ width: { xs: '100%', md: 230 }, flexShrink: 0 }}>
                            <Typography variant="caption" color="text.secondary">
                              Total operación
                            </Typography>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: 900, lineHeight: 1.1, mt: 0.2, mb: 1.5 }}
                            >
                              {formatMoney(total)}
                            </Typography>

                            <Stack spacing={0.9}>
                              {record.synced ? (
                                <Tooltip
                                  title={
                                    puedeImprimirDte
                                      ? 'Abrir o imprimir el DTE/PDF generado por el servidor'
                                      : 'DTE/PDF todavía no disponible'
                                  }
                                >
                                  <span>
                                    <Button
                                      variant="contained"
                                      color="success"
                                      startIcon={<PictureAsPdfIcon />}
                                      disabled={!puedeImprimirDte || loading || syncing}
                                      onClick={() => onImprimirDte?.(record)}
                                      fullWidth
                                      sx={{ fontWeight: 800, textTransform: 'none' }}
                                    >
                                      DTE / PDF
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : (
                                <>
                                  <Button
                                    variant="contained"
                                    color={
                                      record.syncRequiresReview === true
                                        ? 'warning'
                                        : record.syncError
                                          ? 'error'
                                          : 'primary'
                                    }
                                    startIcon={
                                      isRowSyncing ? (
                                        <CircularProgress size={16} color="inherit" />
                                      ) : (
                                        <SyncIcon />
                                      )
                                    }
                                    disabled={!isOnline || loading || syncing || isRowSyncing}
                                    onClick={() => onReintentar?.(record)}
                                    fullWidth
                                    sx={{ fontWeight: 800, textTransform: 'none' }}
                                  >
                                    {isRowSyncing ? 'Sincronizando…' : 'Sincronizar'}
                                  </Button>

                                  <Button
                                    variant="outlined"
                                    startIcon={<PrintIcon />}
                                    onClick={() => onReimprimirContingencia?.(record)}
                                    fullWidth
                                    sx={{ fontWeight: 700, textTransform: 'none' }}
                                  >
                                    Comprobante local
                                  </Button>
                                </>
                              )}

                              <Button
                                variant="text"
                                color="error"
                                startIcon={<DeleteOutlineIcon />}
                                onClick={() => solicitarEliminar(record)}
                                disabled={loading || syncing || isRowSyncing}
                                fullWidth
                                sx={{ fontWeight: 700, textTransform: 'none' }}
                              >
                                Eliminar con PIN
                              </Button>
                            </Stack>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.6,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={onRefresh}
              disabled={loading || syncing}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Verificar conexión
            </Button>

            <Button
              variant="contained"
              startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
              disabled={!isOnline || loading || syncing || totalPendienteAccion === 0}
              onClick={onReintentarTodas}
              sx={{ textTransform: 'none', fontWeight: 800 }}
            >
              Sincronizar pendientes{totalPendienteAccion > 0 ? ` (${totalPendienteAccion})` : ''}
            </Button>

            <Button
              color="error"
              variant="text"
              disabled={loading || syncing || resumen.sincronizadas === 0}
              onClick={solicitarLimpiarSincronizadas}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Limpiar sincronizadas
            </Button>
          </Stack>

          <Button
            variant="outlined"
            onClick={handleClosePrincipal}
            sx={{ textTransform: 'none', fontWeight: 800, minWidth: 96 }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <PinConfirmModal
        open={pinOpen}
        onClose={cerrarPin}
        onVerified={ejecutarAccionAutorizada}
      />
    </>
  );
};

export default POS_VentasContingencia;
